import { useEffect, useState } from 'react';

import { MenuTabLookup, ParentLookup, SideMenuProps } from '@main/core-ui';
import { MenuTab } from '@main/core-ui/src/SideMenu/SideMenuTab';
import { indexTree } from '@main/utils';

export interface UseSideMenu {
  /** Callback to select a tab */
  selectTab: (tab: MenuTab, parent?: MenuTab) => void;
}

export const useSideMenu = ({
  onSelectChange,
  selected,
  tabs,
}: SideMenuProps): UseSideMenu => {
  // Index from child ids to all of its parents
  const [parentLookup, setParentLookup] = useState<ParentLookup>({});

  // Lookup menu item by visual id. Only indexing the lead nodes
  const [lookupMenuTab, setLookupMenuTab] = useState<MenuTabLookup>({});

  // Callback to alert parent of change
  const onChange = (
    newSelected: string,
    newTab: boolean,
    parent?: MenuTab,
  ): void => {
    if (!onSelectChange) {
      return;
    }

    // Grab the expected node
    const selectedNode = lookupMenuTab[newSelected];

    // Determine the parent nodes for the selected value
    const selectedParents = (parentLookup[newSelected] || []).map(
      (parentVisualId) => lookupMenuTab[parentVisualId],
    );

    // Callback
    onSelectChange({
      newMenu: newSelected,
      menuTab: selectedNode,
      parentTabs: selectedParents,
      newTab,
      parentTab: parent,
    });
  };

  // Callback to select a tab
  const selectTab = (tab: MenuTab, parent?: MenuTab): void => {
    const findFirstChild = (t: MenuTab): MenuTab => {
      if (t.children && t.children[0]) {
        return findFirstChild(t.children[0]);
      }
      return t;
    };
    const firstChild = findFirstChild(tab);
    const firstChildId = firstChild.visualId;

    onChange(firstChildId, false, parent);
  };

  // Whenever the tabs change, we create an index of the tree to display the tree
  useEffect(() => {
    const indexedTabs = indexTree(tabs, 'visualId');
    setParentLookup(indexedTabs.parentLookup);
    setLookupMenuTab(indexedTabs.lookupTreeTab);
  }, [tabs, selected]);

  return {
    selectTab,
  };
};
